import React from 'react'
import { cn } from '../../lib/utils'

/**
 * Logo Component for Admin Panel
 * 
 * A flexible logo component that handles different variants, sizes, and themes
 * Based on the frontend logo component but adapted for admin use
 */
const Logo = ({
  variant = 'full', // 'full', 'mark', 'stacked'
  size = 'medium',  // 'small', 'medium', 'large', 'xlarge', 'custom'
  theme = 'auto',   // 'auto', 'light', 'dark'
  className = '',
  alt = 'Crowblock Admin',
  clickable = false,
  onClick,
  ...props
}) => {
  // Size configurations
  const sizeClasses = {
    small: 'h-6 w-auto',    // 24px height
    medium: 'h-8 w-auto',   // 32px height  
    large: 'h-12 w-auto',   // 48px height
    xlarge: 'h-16 w-auto',  // 64px height
    custom: ''              // Use custom className
  }

  // Logo file paths - using CrowBlock Logo 2.png as primary for all themes
  const getLogoPath = (variant, theme) => {
    // Using CrowBlock Logo 2.png as the primary logo for all themes and variants
    return `/images/CrowBlock Logo 2.png`
  }

  // Fallback SVG logo when actual logo files are not available
  const FallbackLogo = () => {
    if (variant === 'mark') {
      return (
        <svg 
          className={cn(sizeClasses[size], className)}
          viewBox="0 0 40 40" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          {...props}
        >
          <rect width="40" height="40" rx="8" fill="url(#gradient)" />
          <path 
            d="M12 20L16 16L20 20L24 16L28 20" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M14 26H26" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round"
          />
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1d62e6" />
              <stop offset="100%" stopColor="#1a56cc" />
            </linearGradient>
          </defs>
        </svg>
      )
    }

    return (
      <div className={cn(
        'flex items-center gap-2',
        sizeClasses[size], 
        className
      )}>
        <svg 
          className="h-full w-auto"
          viewBox="0 0 40 40" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="40" height="40" rx="8" fill="url(#gradient)" />
          <path 
            d="M12 20L16 16L20 20L24 16L28 20" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M14 26H26" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round"
          />
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1d62e6" />
              <stop offset="100%" stopColor="#1a56cc" />
            </linearGradient>
          </defs>
        </svg>
        <span className={cn(
          'font-heading font-bold text-gray-900',
          size === 'small' && 'text-sm',
          size === 'medium' && 'text-base', 
          size === 'large' && 'text-xl',
          size === 'xlarge' && 'text-2xl'
        )}>
          CROWBLOCK
        </span>
      </div>
    )
  }

  const logoPath = getLogoPath(variant, theme)
  
  const LogoImage = () => (
    <img
      src={logoPath}
      alt={alt}
      className={cn(sizeClasses[size], className)}
      onError={(e) => {
        // Fallback to SVG logo if image fails to load
        e.target.style.display = 'none'
        e.target.nextSibling.style.display = 'block'
      }}
      {...props}
    />
  )

  const logoContent = (
    <div className="relative">
      <LogoImage />
      <div style={{ display: 'none' }}>
        <FallbackLogo />
      </div>
    </div>
  )

  if (clickable && onClick) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'flex items-center transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded',
          'touch-manipulation'
        )}
        aria-label={`${alt} logo`}
      >
        {logoContent}
      </button>
    )
  }

  return logoContent
}

// Preset logo components for common use cases
export const LogoFull = (props) => <Logo variant="full" {...props} />
export const LogoMark = (props) => <Logo variant="mark" {...props} />
export const LogoStacked = (props) => <Logo variant="stacked" {...props} />

// Size-specific logos
export const LogoSmall = (props) => <Logo size="small" {...props} />
export const LogoMedium = (props) => <Logo size="medium" {...props} />
export const LogoLarge = (props) => <Logo size="large" {...props} />
export const LogoXLarge = (props) => <Logo size="xlarge" {...props} />

export default Logo